import React, { useEffect, useRef, useState } from "react";
import { Alert, Table, Button } from "react-bootstrap";
import { useToggle } from "../../../hooks";
import { get } from "../../../utils/DeApi";
import Loader from "../../Loader/Loader";
import DeleteIcon from "mdi-react/DeleteIcon";
import EditIcon from "mdi-react/EditIcon";
import "./CikTypeList.scss";
import ErrorHandler from "../../ErrorHandler/ErrorHandler";
import CikModal from "../CikListModal/CikModal";

const CikTypeList = ({
  refresh = false,
  action = [],
  show = false,
  setAlertShow = () => false,
  searchInput,
}) => {
  const [isFetching, toggleIsFetching] =
    useToggle(
      false
    ); /* *** conditional const for showing and hiding loader *** */
  const [ciks, setCiks] = useState([]); /* *** array of all keywords *** */
  const [cik, setCik] =
    useState(null); /* *** a single selected cik value container *** */
  const [selectedCiks, setSelectedCiks] = useState([]);

  const [showModal, toggleShowModal] = useToggle(false);
  const [mode, setMode] = useState("Update");
  const subscribedPromises = useRef([]);
  const [error, setError] = useState(null);
  const [msg, setMsg] = useState(null);
  // const [show, setShow] = useState(props.show);
  const [actionType, setActionType] = useState(null);

  const handleEdit = (cik) => {
    setMode("Update");
    setCik(cik);
  };

  const handleDelete = (cik) => {
    setMode("Delete");
    setCik(cik);
  };

  /* *** setting modal operation ( below ) *** */
  const handleModalClose = (data, mode = "Delete") => {
    toggleShowModal();
    setCik(null);
    if (data) {
      switch (mode) {
        case "Add":
          setCiks([data, ...ciks]);
          setActionType(mode.toUpperCase());
          setMsg("CIK Created Successfully!");
          setAlertShow(true); /* *** showing the alert message *** */
          break;

        case "Update":
          let types = ciks.map((record) => {
            /* *** finding relevant record *** */
            if (record.id === data.id) {
              record.name = data.name; /* *** setting new value here *** */
              record.ticker = data.ticker;
              record.cik = data.cik;
              setAlertShow(true);
            }
            return record;
          });
          setCiks(
            types.sort((a, b) => {
              if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
              if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
              return 0;
            })
          );

          setActionType(mode.toUpperCase());
          setAlertShow(true); /* *** showing the alert message *** */
          setMsg("CIK Updated Successfully!");
          break;

        case "Delete":
          setCiks(
            ciks.filter((rec) => rec.id !== data.id)
          ); /* *** excluding requested record from the record array *** */
          setAlertShow(true); /* *** showing the alert message *** */
          setActionType(mode.toUpperCase());
          setMsg("CIK Deleted Successfully!");
          break;

        default:
          return;
      }
    }
  }; /* *** end handle-modal-close *** */

  const resetAlert = () => {
    setMsg("");
    setAlertShow(false);
    setMode("");
    setActionType("");
  };

  useEffect(() => {
    if (
      action &&
      action.response &&
      Object.keys(action.response.data).length > 0
    ) {
      let data = action.response.data;
      setMsg(action.response.msg ? action.response.msg : "");
      setActionType("Add");
      setMsg("CIK Created Successfully!");
      if (action.response.msg !== "CIK Already Exists!") {
        setCiks((prevState) => {
          const updatedState = [...prevState, data];
          return updatedState.sort((a, b) => {
            if (a.name.toLowerCase() < b.name.toLowerCase()) return -1;
            if (a.name.toLowerCase() > b.name.toLowerCase()) return 1;
            return 0;
          });
        });
      }
    }
  }, [refresh, toggleIsFetching]);

  /* *** making sure to trigger toggle-show-modal() *** */
  useEffect(() => {
    if (cik?.name) {
      toggleShowModal();
    }
  }, [cik, toggleShowModal]);

  useEffect(() => {
    if (ciks && ciks.length <= 0) {
      const { promise, cancel } = get("ciks");
      toggleIsFetching();
      promise
        .then((data) => {
          let records = data.data;
          setCiks(
            records.sort((a, b) => {
              if (a.name.toLowerCase() < b.name.toLowerCase()) {
                return -1;
              }
              if (a.name.toLowerCase() > b.name.toLowerCase()) {
                return 1;
              }
              return 0;
            })
          );
        })
        .catch((error) => setError(error))
        .finally(toggleIsFetching);
      subscribedPromises.current.push(
        cancel
      ); /* *** updating array with cancelable of promise *** */
    }
  }, [refresh, toggleIsFetching]);

  useEffect(() => {
    const promises = subscribedPromises.current;
    return () => promises.forEach((cancel) => cancel());
  }, []);

  useEffect(() => {
    setSelectedCiks(ciks);
  }, [ciks]);

  useEffect(() => {
    if (searchInput?.trim().length > 0) {
      const filteredCiks = ciks.filter((cik) =>
        cik.name.toLowerCase().includes(searchInput.toLowerCase())
      );
      setSelectedCiks(filteredCiks);
    } else {
      setSelectedCiks(ciks);
    }
  }, [searchInput]);

  /* *** check either fetching is finished or not *** */
  if (isFetching) {
    return <Loader />;
  }

  /* *** check if no data found *** */
  if (ciks && ciks.length === 0) {
    return <NoData />;
  }

  const AlertMsg = () => {
    return (
      <Alert variant="success" onClose={() => resetAlert()} dismissible>
        <Alert.Heading>CIK {actionType}</Alert.Heading>
        <p>{msg}</p>
      </Alert>
    );
  };
  /* *** returning content *** */
  return (
    <>
      {error && <ErrorHandler error={error} />}
      {show && <AlertMsg />}
      <Table style={{ width: "100%", marginTop: "5px" }}>
        <thead className="responsive-th-optimized-search">
          <tr>
            <th className="srWidth">Sr.</th>
            <th className="nameWidth">BDC Names</th>
            <th className="tblCol">TICKER</th>
            <th className="tblCol">CIK</th>
            <th className="actionWidth" style={{ paddingRight: "1.5%" }}>
              Action
            </th>
          </tr>
        </thead>
        <tbody>
          {selectedCiks &&
            selectedCiks.length > 0 &&
            selectedCiks?.map((keyType, index) => (
              <CikRow
                key={index}
                counter={index}
                keywType={keyType}
                onEdit={handleEdit}
                onDelete={handleDelete}
              />
            ))}
        </tbody>
      </Table>
      <CikModal
        mode={mode}
        cikObject={cik}
        show={showModal}
        onClose={handleModalClose}
      />
    </>
  );
};

const CikRow = ({ keywType, onEdit, onDelete, counter }) => {
  return (
    <tr>
      <td>{counter + 1}</td>
      <td>{keywType.name}</td>
      <td>{keywType.ticker}</td>
      <td>{keywType.cik}</td>
      <td className="actionWidth">
        {/* edit button is below */}
        <Button
          variant="null"
          size="sm"
          className="p-1 color-deal-style"
          onClick={() => onEdit(keywType)}
        >
          <EditIcon size={20} />
        </Button>
        {/* delete button is below */}
        <Button
          variant="null"
          size="sm"
          className="p-1 color-deal-style"
          onClick={() => onDelete(keywType)}
        >
          <DeleteIcon size={20} />
        </Button>
      </td>
    </tr>
  );
};

const NoData = () => {
  return (
    <Alert className="mt-5" variant="info">
      <h4 className="mb-0">No CIKs to Show.</h4>
    </Alert>
  );
};

export default CikTypeList;
